import React, { useState, useRef } from 'react';
import Slider from 'react-slick';

import { STYLES } from './constants';

import './BodyFreaksStore.scss';

export const BodyFreaksStore = ({ baseImgUrl }) => {
  const [statePhotochromicMetal, setStatePhotochromicMetal] = useState({
    currentKey: 'C5',
    colors: STYLES.photochromicMetal.product.colors,
  });
  const [statePhotochromicPlastic, setStatePhotochromicPlastic] = useState({
    currentKey: 'C5',
    colors: STYLES.photochromicPlastic.product.colors,
  });
  const [stateFolding, setStateFolding] = useState({
    currentKey: 'C1',
    colors: STYLES.folding.product.colors,
  });
  const [statePolarised, setStatePolarised] = useState({
    currentKey: 'C3',
    colors: STYLES.polarised.product.colors,
  });

  const refPhotochromicMetal = useRef(null);
  const refPhotochromicPlastic = useRef(null);
  const refFolding = useRef(null);
  const refPolarised = useRef(null);

  const previewImgSetting = {
    infinite: true,
    dots: true,
    arrows: true,
    speed: 500,
    slidesToShow: 1,
  };

  const productSetting = {
    infinite: true,
    dots: false,
    speed: 500,
    arrows: false,
    centerMode: true,
    variableWidth: true,
  };

  const photochromicMetalSetting = {
    ...productSetting,
    afterChange: async current => {
      await Promise.all([setStatePhotochromicMetal(prev => ({ ...prev }))]);
    },
  };

  const photochromicPlasticSetting = {
    ...productSetting,
    afterChange: async current => {
      await Promise.all([setStatePhotochromicPlastic(prev => ({ ...prev }))]);
    },
  };

  const foldingSetting = {
    ...productSetting,
    afterChange: async current => {
      await Promise.all([setStateFolding(prev => ({ ...prev }))]);
    },
  };

  const polarisedSetting = {
    ...productSetting,
    afterChange: async current => {
      await Promise.all([setStatePolarised(prev => ({ ...prev }))]);
    },
  };

  const productMap = {
    'FK1003G-5S': {
      setting: photochromicMetalSetting,
      ref: refPhotochromicMetal,
      limit: STYLES.photochromicMetal.product.limit,
      currentKey: statePhotochromicMetal.currentKey,
      stateProduct: statePhotochromicMetal,
      setState: setStatePhotochromicMetal,
    },
    'FK2005B-5S': {
      setting: photochromicPlasticSetting,
      ref: refPhotochromicPlastic,
      limit: STYLES.photochromicPlastic.product.limit,
      currentKey: statePhotochromicPlastic.currentKey,
      stateProduct: statePhotochromicPlastic,
      setState: setStatePhotochromicPlastic,
    },
    'FK2004N-5S': {
      setting: foldingSetting,
      ref: refFolding,
      limit: STYLES.folding.product.limit,
      currentKey: stateFolding.currentKey,
      stateProduct: stateFolding,
      setState: setStateFolding,
    },
    'FK2003N-5S': {
      setting: polarisedSetting,
      ref: refPolarised,
      limit: STYLES.polarised.product.limit,
      currentKey: statePolarised.currentKey,
      stateProduct: statePolarised,
      setState: setStatePolarised,
    },
  };

  const selectedIndexMap = {
    C1: 0,
    C4: 0,
    C2: 1,
    C5: 1,
    C3: 2,
    C6: 2,
  };

  const renderMainStyle = item => {
    return (
      <div className="body-freaks-store__styles__item-layout">
        <div className="body-freaks-store__styles__item-header">
          <h2 data-aos="fade-up">
            <span className="body-freaks-store__styles__item-no">{item.no}</span>
            <span className="body-freaks-store__styles__item-title">{item.title}</span>
            <span className="body-freaks-store__styles__item-frame">{item.frame}</span>
          </h2>
        </div>

        <div className="body-freaks-store__styles__item-main" data-aos="fade-up">
          <picture>
            <source
              media="(min-width: 768px)"
              srcSet={`${baseImgUrl}/${item.imgPath}/main_pc.webp`}
            />
            <img
              alt={item.alt.main}
              loading="lazy"
              src={`${baseImgUrl}${item.imgPath}/main_sp.webp`}
            />
          </picture>

          <p>{item.desc}</p>
        </div>

        <Slider {...previewImgSetting} className="body-freaks-store__styles__item-preview">
          {Array.from({ length: item.previewLimit || 4 }, (_, index) => (
            <li key={`preview-freaks-store-${index + 1}`}>
              {item?.version ? (
                <img
                  alt={item.alt.sub}
                  loading="lazy"
                  src={`${baseImgUrl}${item.imgPath}/preview-${index + 1}_${item?.version}.webp`}
                />
              ) : (
                <img
                  alt={item.alt.sub}
                  loading="lazy"
                  src={`${baseImgUrl}${item.imgPath}/preview-${index + 1}.webp`}
                />
              )}
            </li>
          ))}
        </Slider>
      </div>
    );
  };

  const isSelectedColor = (currentKey, index) => {
    const selectedColorMap = {
      0: ['C1', 'C4'],
      1: ['C2', 'C5'],
      2: ['C3', 'C6'],
    };
    return selectedColorMap[index]?.includes(currentKey) || false;
  };

  const handleSelectedColor = (productNumber, index) => {
    if (productMap[productNumber]) {
      const { setState } = productMap[productNumber];
      const productColorKeyMap = {
        'FK1003G-5S': ['C4', 'C5', 'C6'],
        'FK2005B-5S': ['C4', 'C5', 'C6'],
        'FK2004N-5S': ['C1', 'C2', 'C3'],
        'FK2003N-5S': ['C1', 'C2', 'C3'],
      };

      const colorKeys = productColorKeyMap[productNumber];
      const newCurrentKey = colorKeys?.[index] || null;

      setState(prev => ({
        ...prev,
        currentKey: newCurrentKey,
      }));
    }
  };

  const goToPrev = sliderRef => {
    if (sliderRef.current) {
      sliderRef.current.slickPrev();
    }
  };

  const goToNext = sliderRef => {
    if (sliderRef.current) {
      sliderRef.current.slickNext();
    }
  };

  const renderProductDesc = item => {
    const { number, price, limit, startIndex } = item.product;
    const { currentKey, stateProduct, setting, ref } = productMap[number];

    const nameColor = stateProduct.colors[selectedIndexMap[currentKey]];

    return (
      <div className="body-freaks-store__styles__product">
        <Slider {...setting} ref={ref} className="body-freaks-store__styles__product-list">
          {Array.from({ length: limit }, (_, index) => (
            <li key={`product-freaks-store-${index}`}>
              <img
                alt={`${number} ${currentKey}`}
                src={`${baseImgUrl}/products/${number}/${currentKey}/${number}_${currentKey}_0${
                  index + startIndex
                }.webp`}
              />
            </li>
          ))}
        </Slider>

        <div className="body-freaks-store__styles__product-desc">
          <div className="body-freaks-store__styles__product-desc__box">
            <div className="body-freaks-store__styles__product-desc__text">
              <p className="body-freaks-store__styles__product-desc__text-no">{number}</p>
              <p className="body-freaks-store__styles__product-desc__text-color-name">{`${currentKey} ${nameColor}`}</p>
              <p className="body-freaks-store__styles__product-desc__text-price">{price}</p>
            </div>

            <div className="body-freaks-store__styles__product-desc__footer">
              <ul className="body-freaks-store__styles__product-desc__colors">
                {stateProduct.colors.map((color, index) => (
                  <li key={color}>
                    <button
                      data-color={color.toLowerCase().replace(' ', '-')}
                      data-color-selected={isSelectedColor(currentKey, index)}
                      onClick={() => handleSelectedColor(number, index)}
                    />
                  </li>
                ))}
              </ul>

              <div className="body-freaks-store__styles__product__arrows">
                <button
                  className="body-freaks-store__styles__product__arrows-prev"
                  onClick={() => goToPrev(ref)}
                />
                <button
                  className="body-freaks-store__styles__product__arrows-next"
                  onClick={() => goToNext(ref)}
                />
              </div>
            </div>
          </div>

          {/* <a
            disabled
            className="base-freaks-store-2025__btn"
            href={`/sg/en/freaks-store-${number.toLowerCase()}-${currentKey.toLowerCase()}-sunglasses.html`}
          >
            Purchase Online
          </a> */}
        </div>
      </div>
    );
  };

  const renderSpecifications = item => {
    const { product, remark = null } = item;
    const { number, specifications } = product;
    const { currentKey } = productMap[number];

    const specText = index => {
      const { visibleLightTransmittance, uvTransmittance, lensMaterial } =
        specifications[selectedIndexMap[currentKey]];

      switch (index) {
        case 0:
          return `Visible Light Transmittance: ${visibleLightTransmittance}`;

        case 1:
          return `UV Transmittance: ${uvTransmittance}`;

        case 2:
          return `Lens Material: ${lensMaterial}`;

        default:
          return null;
      }
    };

    return (
      <div className="base-freaks-store-2025__container">
        <div className="body-freaks-store__styles__product__specifications">
          <dl>
            <dt>Specifications</dt>
            <dd>
              <ul>
                {Array.from({ length: 3 }, (_, index) => (
                  <li key={index}>{specText(index)}</li>
                ))}
              </ul>
            </dd>
          </dl>

          {remark && (
            <div className="body-freaks-store__styles__product__specifications__remark-layout">
              <p className="body-freaks-store__styles__product__specifications__remark">{remark}</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <section className="body-freaks-store__styles">
      {['photochromicMetal', 'photochromicPlastic', 'folding', 'polarised'].map(key => {
        const style = STYLES[key];

        return (
          <div
            key={style.id}
            className="body-freaks-store__styles__item"
            data-key={style.id}
            id={style.id}
          >
            {renderMainStyle(style)}
            {renderProductDesc(style)}
            {renderSpecifications(style)}
          </div>
        );
      })}
    </section>
  );
};
